import '../../styles/interactives.scss'
import { useCallback, useEffect, useRef } from 'react'
const threshold = [0.5, 0.6, 0.7, 0.85]

export default function UnequalDistribution() {
  const paragraphs = useRef(null)
  const interactive = useRef(null)

  const handleSteps = useCallback(
    (entries) => {
      entries.forEach((entry) => {
        // check that we have a ref, and that we are measuring one way only
        if (!interactive?.current || entry?.boundingClientRect.y < 0) return
        const ratio = entry?.intersectionRatio ?? 0

        // On Mount the observer get called with a 0 value
        if (ratio === 0) return

        const paragraph = Number(entry?.target.getAttribute('data-index') ?? 0)
        if (paragraph === 0) return

        const beforeP1 = paragraph === 1 && ratio < threshold[0]
        const industries0 =
          paragraph === 1 && ratio > threshold[0] && ratio < threshold[1]
        const industries1 =
          paragraph === 1 && ratio > threshold[1] && ratio < threshold[2]
        const industries2 =
          paragraph === 1 && ratio > threshold[2] && ratio < threshold[3]
        const industries3 = paragraph === 1 && ratio > threshold[3]

        const beforeP2 = paragraph === 2 && ratio < threshold[2]
        const afterP2 = paragraph === 2 && ratio > threshold[3]

        const beforeP3 = paragraph === 3 && ratio < threshold[2]
        const afterP3 = paragraph === 3 && ratio > threshold[3]

        if (beforeP1) {
          interactive.current.removeAttribute('data-step')
        } else if (industries0) {
          interactive.current.setAttribute('data-step', 1)
        } else if (industries1) {
          interactive.current.setAttribute('data-step', 2)
        } else if (industries2) {
          interactive.current.setAttribute('data-step', 3)
        } else if (industries3 || beforeP2) {
          interactive.current.setAttribute('data-step', 4)
        } else if (afterP2 || beforeP3) {
          interactive.current.setAttribute('data-step', 5)
        } else if (afterP3) {
          interactive.current.setAttribute('data-step', 6)
        }
      })
    },
    [interactive]
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleSteps, { threshold })

    if (paragraphs?.current) {
      paragraphs.current.querySelectorAll('li').forEach((paragraph, index) => {
        paragraph.setAttribute('data-index', index + 1)
        observer.observe(paragraph)
      })
    }

    return () => observer.disconnect()
  }, [handleSteps, paragraphs])

  return (
    <section className="interactive" name="Unequal Distribution">
      <header>
        <h2>
          That matters because even amongst first-gen students, exposure{' '}
          <strong> isn’t equally distributed</strong>
        </h2>
      </header>
      <main>
        <aside>
          <figure ref={interactive} className="unequal-distribution">
            <ol title="Exposure">
              <li>0 industries</li>
              <li>1</li>
              <li>2</li>
              <li>3+</li>
            </ol>
            <ol title="GPA">
              <li>
                <span>Below 2.5</span>
                <div>
                  <span>46%</span>
                </div>
                <div></div>
                <div></div>
                <div></div>
              </li>
              <li>
                <span>2.5-3.5</span>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </li>
              <li>
                <span>3.5+</span>
                <div>
                  <span>35%</span>
                </div>
                <div></div>
                <div></div>
                <div></div>
              </li>
            </ol>
            <ol title="Percentage of Students">
              <li>
                <span>0%</span>
              </li>
              <li>
                <span>20%</span>
              </li>
              <li>
                <span>40%</span>
              </li>
              <li>
                <span>60%</span>
              </li>
              <li>
                <span>80%</span>
              </li>
              <li>
                <span>100%</span>
              </li>
            </ol>
          </figure>
        </aside>
        <ul ref={paragraphs}>
          <li>
            <p>
              Students with GPAs below 2.5 are more likely to{' '}
              <strong> have less exposure and knowledge of industries</strong>
            </p>
          </li>
          <li>
            <p>
              Almost half of all students with a GPA below 2.5{' '}
              <strong>
                don’t report significant exposure to any industries
              </strong>
            </p>
          </li>
          <li>
            <p>
              But, it’s not just about academics: even in the highest GPA band,
              35% of students{' '}
              <strong>
                don’t report significant exposure to any industries
              </strong>
            </p>
          </li>
        </ul>
      </main>
    </section>
  )
}
